import { useCallback, useRef, type KeyboardEvent, type PointerEvent } from 'react'
import { twMerge } from 'tailwind-merge'
import { useDragCursor } from '../../../hooks/use-drag-cursor'
import { useMeetingNotepadResize } from '../use-meeting-notepad-resize'

/** One press of an arrow key. A line of notes, roughly. */
const KEYBOARD_STEP_PX = 16

/**
 * The notepad's top edge, as something you can drag.
 *
 * The panel is bottom-anchored, so the top edge is the only one that can move —
 * dragging it up is the notepad taking room from the view behind it, which is
 * the same trip the full-height button makes in one press. The button stays:
 * the drag is for landing somewhere in between, not for replacing it.
 *
 * The grabber sits in the card's top padding and stops short of the title row's
 * own hit area, so grabbing the edge can't collapse the notepad by accident.
 *
 * Pointer capture is the whole event story: once the handle holds the pointer,
 * every later move and the release retarget to it, so a fast drag that outruns
 * the edge — or crosses the view behind — keeps resizing without a single
 * document listener to attach, tear down, or leak on unmount.
 */
export function NotepadResizeHandle() {
  const { height, minHeight, maxHeight, isDragging, resize, startDrag, endDrag } = useMeetingNotepadResize()
  const startYRef = useRef(0)
  const startHeightRef = useRef(0)

  useDragCursor(isDragging, 'ns-resize')

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      startYRef.current = e.clientY
      startHeightRef.current = height
      startDrag()
    },
    [height, startDrag],
  )

  // Capture is also what says a drag is under way — it's set synchronously in
  // the same handler, so this can't miss a move that beats the state write.
  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      // Up is taller: the panel grows out of a fixed bottom edge.
      resize(startHeightRef.current + (startYRef.current - e.clientY))
    },
    [resize],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Home/End are the ends of the range, per the APG slider pattern.
      const next =
        e.key === 'ArrowUp'
          ? height + KEYBOARD_STEP_PX
          : e.key === 'ArrowDown'
            ? height - KEYBOARD_STEP_PX
            : e.key === 'Home'
              ? minHeight
              : e.key === 'End'
                ? maxHeight
                : null
      if (next === null) return
      e.preventDefault()
      resize(next)
    },
    [height, minHeight, maxHeight, resize],
  )

  return (
    <div
      // `slider`, not the window-splitter's focusable `separator`: the two are
      // the same widget to a screen reader (a value between two bounds, moved
      // with the arrow keys), and this is the one the a11y lint recognises as
      // interactive — a `separator` carrying a tabindex reads to it as a
      // non-interactive element handed keyboard listeners.
      role="slider"
      aria-orientation="vertical"
      aria-label="Resize notepad"
      aria-valuenow={Math.round(height)}
      aria-valuemin={minHeight}
      aria-valuemax={Math.round(maxHeight)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      // Release, cancellation (a system dialog stealing the pointer) and any
      // other way capture can be lost all end the same gesture.
      onLostPointerCapture={endDrag}
      onKeyDown={handleKeyDown}
      className={twMerge(
        // Base UI's drawer grabber: 48×4, centred, square, and always showing —
        // a drawer's handle is how you know the surface moves, so it isn't a
        // control that appears on hover. Its flat greys are `oklch(87% 0 0)`
        // light and `oklch(37.1% 0 0)` dark, which the foreground token lands on
        // from either side without a second colour to keep in step.
        //
        // Centred in the card's top padding by `inset-x-0` + `mx-auto`, which
        // is what lets the element be the bar itself rather than a strip around
        // one — the hit area is the pseudo-element's job.
        'absolute inset-x-0 top-2 z-10 mx-auto h-1 w-12 cursor-ns-resize touch-none',
        'bg-element-active',
        // A 4px bar is nothing to aim at, so the target is grown around it
        // rather than the bar being drawn bigger than the drawer's. 24px past
        // each end, and vertically it takes the card's top edge down to where
        // the title row starts — asymmetric because the row owns everything
        // below that, and two live targets in the same band is a coin toss.
        "after:absolute after:-inset-x-6 after:-bottom-1 after:-top-2 after:content-['']",
      )}
    />
  )
}
